import React, { useState, useEffect } from 'react'
import { X, Trash2, Play, Volume2, Eye, Calendar, MessageCircle } from 'lucide-react'

interface SavedFoot {
  id: string
  name: string
  prompt: string
  filters: Record<string, any>
  imageUrl: string
  createdAt: string
  timesUsed: number
}

interface SavedFeetManagerProps {
  isOpen: boolean
  onClose: () => void
}

const SavedFeetManager: React.FC<SavedFeetManagerProps> = ({ isOpen, onClose }) => {
  const [savedFeet, setSavedFeet] = useState<SavedFoot[]>([])
  const [selectedFoot, setSelectedFoot] = useState<SavedFoot | null>(null)
  const [footToDelete, setFootToDelete] = useState<SavedFoot | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isPlaying, setIsPlaying] = useState<string | null>(null)

  useEffect(() => {
    loadSavedFeet()
  }, [isOpen])

  const loadSavedFeet = () => {
    const saved = localStorage.getItem('toefettiSavedFeet')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        // Add IDs and metadata if missing
        const withIds = parsed.map((foot: any, index: number) => ({
          id: foot.id || `foot-${index}`,
          name: foot.name,
          prompt: foot.prompt,
          filters: foot.filters || {},
          imageUrl: foot.imageUrl,
          createdAt: foot.createdAt || new Date().toISOString(),
          timesUsed: foot.timesUsed || 0
        }))
        setSavedFeet(withIds)
      } catch (error) {
        console.error('Error loading saved feet:', error)
        setSavedFeet([])
      }
    }
  }

  const saveFeetToStorage = (feet: SavedFoot[]) => {
    localStorage.setItem('toefettiSavedFeet', JSON.stringify(feet))
    setSavedFeet(feet)
  }

  const deleteFoot = (footId: string) => {
    const updated = savedFeet.filter(foot => foot.id !== footId)
    saveFeetToStorage(updated)
    setFootToDelete(null)
    if (selectedFoot?.id === footId) {
      setSelectedFoot(null)
    }
  }

  const speak = async (foot: SavedFoot) => {
    if ('speechSynthesis' in window) {
      // Update usage count
      const updated = savedFeet.map(f => 
        f.id === foot.id ? { ...f, timesUsed: f.timesUsed + 1 } : f
      )
      saveFeetToStorage(updated)

      setIsPlaying(foot.id)
      const utter = new window.SpeechSynthesisUtterance(foot.prompt)
      utter.rate = 1.05
      utter.pitch = 1.1
      utter.lang = 'en-US'
      utter.onend = () => setIsPlaying(null)
      utter.onerror = () => setIsPlaying(null)
      window.speechSynthesis.speak(utter)
    } else {
      alert('Speech synthesis not supported in this browser.')
    }
  }

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel()
      setIsPlaying(null)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    })
  }

  const filteredFeet = savedFeet.filter(foot =>
    foot.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    foot.prompt.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-t-3xl md:rounded-2xl shadow-xl w-full h-full md:w-full md:max-w-4xl md:h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white rounded-t-3xl md:rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-bold text-pink-700">Your Saved Feet</h2>
            <span className="px-2 py-1 bg-pink-100 text-pink-600 rounded-full text-sm font-medium">
              {filteredFeet.length} personas
            </span>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200 bg-pink-50">
          <input
            type="text"
            placeholder="Search your foot personas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-pink-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent"
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredFeet.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 text-pink-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchQuery ? 'No matching personas found' : 'No saved foot personas yet'}
              </h3>
              <p className="text-gray-500">
                {searchQuery ? 'Try adjusting your search terms' : 'Create your first foot persona by generating an image and saving it!'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredFeet.map((foot) => (
                <div key={foot.id} className="bg-white border border-pink-200 rounded-2xl overflow-hidden hover:border-pink-300 transition-colors">
                  {/* Foot Image */}
                  <div className="relative h-40">
                    <img
                      src={foot.imageUrl}
                      alt={foot.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    
                    {/* Overlay Actions */}
                    <div className="absolute top-3 right-3 flex space-x-2">
                      <button
                        onClick={() => setSelectedFoot(foot)}
                        className="p-2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
                      >
                        <Eye className="w-4 h-4 text-gray-700" />
                      </button>
                      <button
                        onClick={() => setFootToDelete(foot)}
                        className="p-2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-red-50 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-gray-700" />
                      </button>
                    </div>

                    {/* Foot Name */}
                    <div className="absolute bottom-3 left-3 right-3">
                      <h3 className="text-white font-bold text-lg truncate">{foot.name}</h3>
                    </div>
                  </div>

                  {/* Foot Details */}
                  <div className="p-4">
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{foot.prompt}</p>
                    
                    {/* Stats */}
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(foot.createdAt)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Volume2 className="w-3 h-3" />
                        <span>{foot.timesUsed} times used</span>
                      </div>
                    </div>

                    {/* Action Button */}
                    <button
                      onClick={() => isPlaying === foot.id ? stopSpeaking() : speak(foot)}
                      disabled={isPlaying !== null && isPlaying !== foot.id}
                      className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-xl font-medium transition-colors ${
                        isPlaying === foot.id
                          ? 'bg-red-500 text-white hover:bg-red-600'
                          : 'bg-pink-500 text-white hover:bg-pink-600 disabled:bg-gray-300 disabled:text-gray-500'
                      }`}
                    >
                      {isPlaying === foot.id ? (
                        <>
                          <X className="w-4 h-4" />
                          <span>Stop Talking</span>
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4" />
                          <span>Make {foot.name} Talk</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Foot Detail Modal */}
        {selectedFoot && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-pink-700">{selectedFoot.name}</h2>
                <button 
                  onClick={() => setSelectedFoot(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Image */}
                <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden">
                  <img
                    src={selectedFoot.imageUrl}
                    alt={selectedFoot.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Details */}
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Persona Prompt</h3>
                    <div className="p-3 bg-pink-50 rounded-lg">
                      <p className="text-gray-700">{selectedFoot.prompt}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <div className="text-lg font-bold text-blue-600">{selectedFoot.timesUsed}</div>
                      <div className="text-xs text-blue-500">Times Used</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-lg font-bold text-green-600">{formatDate(selectedFoot.createdAt)}</div>
                      <div className="text-xs text-green-500">Created</div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <button
                    onClick={() => isPlaying === selectedFoot.id ? stopSpeaking() : speak(selectedFoot)}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-xl font-medium transition-colors ${
                      isPlaying === selectedFoot.id
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : 'bg-pink-500 text-white hover:bg-pink-600'
                    }`}
                  >
                    {isPlaying === selectedFoot.id ? (
                      <>
                        <X className="w-4 h-4" />
                        <span>Stop</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4" />
                        <span>Talk</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setFootToDelete(selectedFoot)}
                    className="px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl font-medium transition-colors flex items-center space-x-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {footToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Foot Persona</h3>
                <p className="text-gray-600 mb-4">
                  Are you sure you want to delete "{footToDelete.name}"? This action cannot be undone.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setFootToDelete(null)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => deleteFoot(footToDelete.id)}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SavedFeetManager 